import { useState } from 'react';
import { giftHref, giftIsLive, useGifts, type GiftOption } from '../lib/content';
import OfflinePill from '../components/OfflinePill';
import { IconCheck, IconExternal, IconGift } from '../components/Icon';

/**
 * Gifts (D63).
 *
 * Two ways to give something, split by **where a guest banks** rather than by
 * where they are from: "international" and "Indian" was the first draft of
 * these headings and it sorted people into boxes they did not pick. Half this
 * guest list holds two passports.
 *
 * **The fifth header tab** (D63 as amended). D62 measured five labelled tabs
 * as what fits at 375px and then chose to leave the fifth empty; this spends
 * it, and the measurement reproduces unchanged because "Gifts" is five
 * characters. Three features were refused that slot before this one took it
 * (D54, D57, D62), so the seat is gone rather than being held in reserve —
 * see `Header.tsx`, which carries the arithmetic. Also reached from Profile,
 * and from a link pasted into WhatsApp, which D31 names as how things get
 * announced here; that last one is why the route is in `lib/intent`.
 *
 * Everything on the page is content (D12/D13) because the two halves are not
 * ready at the same time: the Zola registry has existed for months and the UPI
 * address does not exist yet. A card with nothing behind it renders as a note
 * rather than a dead button — see `giftIsLive`.
 */
export default function Gifts() {
  const { data, fetchedAt, error } = useGifts();

  if (!data) {
    return (
      <div className="page page-narrow">
        {error ? (
          <div className="empty">
            <IconGift size={40} className="mark" />
            <h4 style={{ margin: 0 }}>This page didn't load</h4>
            <p>Nothing is wrong on your end, and nothing here is urgent. Try again in a moment.</p>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="page page-narrow gift-page">
      <OfflinePill fetchedAt={fetchedAt} />

      <div className="gift-head">
        <h2>{data.title}</h2>
        <p>{data.intro}</p>
      </div>

      <div className="gift-grid">
        {data.options.map((option) => <GiftCard key={option.id} option={option} />)}
      </div>

      {data.note ? <p className="gift-note">{data.note}</p> : null}
    </div>
  );
}

function GiftCard({ option }: { option: GiftOption }) {
  const live = giftIsLive(option);
  const url = giftHref(option);
  const upiId = option.upiId?.trim();

  return (
    <section className={live ? 'gift-card' : 'gift-card gift-card-soon'}>
      {option.image ? (
        <picture>
          {/* Built together by `scripts/build-images.mjs`, which is why the
              content names one path and this appends the three extensions. */}
          <source type="image/avif" srcSet={`${option.image}.avif`} />
          <source type="image/webp" srcSet={`${option.image}.webp`} />
          {/* `alt=""` deliberately: the photograph is decoration. It carries
              nothing the heading and blurb do not already say, and describing
              it to a screen reader would put a paragraph about a wooden table
              between a guest and the only button on the card. */}
          <img src={`${option.image}.jpg`} alt="" width={700} height={930} loading="lazy" />
        </picture>
      ) : null}

      <div className="gift-face">
        <p className="gift-audience">{option.audience}</p>
        <h3>{option.label}</h3>
        <p className="gift-blurb">{option.blurb}</p>

        {url ? (
          <a className="btn btn-primary" href={url} target="_blank" rel="noreferrer noopener">
            {option.cta ?? 'Open'} <IconExternal size={14} />
          </a>
        ) : null}

        {upiId ? <CopyAddress address={upiId} /> : null}

        {!live && option.pending ? <p className="gift-pending">{option.pending}</p> : null}
      </div>
    </section>
  );
}

/**
 * The UPI address as text, with a copy button.
 *
 * The button is the convenience; **the visible address is the thing that
 * works.** A `upi://` link opens the app chooser on Android and does nothing at
 * all on an iPhone, and `navigator.clipboard` is unavailable on an insecure
 * origin and refusable everywhere — so the address is on screen, selectable, in
 * a face you can read a dot from, whatever either of those does.
 */
function CopyAddress({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard?.writeText(address).then(
      () => {
        setCopied(true);
        // No cleanup on unmount: the worst case is a `setState` on a component
        // that has gone, which React 18 no longer warns about and which cannot
        // be reached anyway — leaving this page unmounts the button with it.
        setTimeout(() => setCopied(false), 2000);
      },
      // A refused clipboard is not an error worth showing. The address is
      // already on screen and can be selected by hand.
      () => {},
    );
  };

  return (
    <div className="gift-upi">
      <code>{address}</code>
      <button type="button" className="btn btn-secondary btn-tiny" onClick={copy}>
        {copied ? <><IconCheck size={13} /> Copied</> : 'Copy'}
      </button>
    </div>
  );
}
